import { useState, useEffect, useCallback } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableToken,
  EditableSlotGroup,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Slot group definitions ───────────────────────────────────────────────────

// Optional click-through link. When a URL is set, the popup image becomes a
// link (e.g. to the apply / contact page or a tel: number). Leave the URL blank
// to render a non-clickable image.
const linkDef: SlotGroupDefinition = {
  label: 'Popup Link (optional)',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// sessionStorage flag so the popup shows only once per visit (cleared when the
// browser tab/session ends).
const SEEN_KEY = 'alpp-promo-popup-seen';

// ─── Component ────────────────────────────────────────────────────────────────

export function PromoPopup() {
  // A single, manually-set image per property. Blank by default — the property
  // manager uploads the promo artwork in the editor.
  const image = useContentToken('promopopup.image', { defaultValue: '' });
  const link = useSlotGroup('promopopup.link', linkDef);

  // Closed during SSR / first paint, then opened on mount so it shows once per
  // visit (session) — across any page — without flashing in the static HTML.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let seen = false;
    try {
      seen = window.sessionStorage.getItem(SEEN_KEY) === '1';
    } catch {
      // sessionStorage unavailable (private mode / blocked) — fall back to
      // showing the popup rather than suppressing it.
    }
    if (seen) return;
    const raf = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    try {
      window.sessionStorage.setItem(SEEN_KEY, '1');
    } catch {
      // Ignore storage failures — the popup simply won't be remembered.
    }
  }, []);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;

  const hasLink = Boolean(link.url && link.url.trim());

  const imageEl = (
    <EditableToken
      path="promopopup.image"
      type="image"
      as="div"
      label="Popup Image"
      className="alpp-image-wrap"
    >
      <VibesitesImage
        url={image || ''}
        alt="Special offer"
        priority
        className="alpp-image"
      />
    </EditableToken>
  );

  return (
    <div
      className="alpp-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Promotion"
      onClick={close}
    >
      <div className="alpp-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="alpp-close" onClick={close} aria-label="Close">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <EditableSlotGroup groupPath="promopopup.link" definition={linkDef}>
          {hasLink ? (
            <a href={link.url || '#'} target={link._target || '_self'} className="alpp-link-a">
              {imageEl}
            </a>
          ) : (
            imageEl
          )}
        </EditableSlotGroup>
      </div>

      <style>{`
        .alpp-overlay {
          position: fixed;
          inset: 0;
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.25rem;
          background: rgba(0, 0, 0, 0);
          animation: alpp-overlay-in 0.3s ease forwards;
        }
        @keyframes alpp-overlay-in {
          to { background: rgba(0, 0, 0, 0.6); }
        }

        .alpp-modal {
          position: relative;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          opacity: 0;
          transform: translateY(16px) scale(0.97);
          animation: alpp-modal-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) 0.05s forwards;
        }
        @keyframes alpp-modal-in {
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .alpp-link-a { display: block; }

        .alpp-image-wrap {
          display: block;
          width: 100%;
        }

        .alpp-image {
          display: block;
          width: 100%;
          height: auto;
          min-height: 280px;
          max-height: 90vh;
          object-fit: contain;
          border-radius: 4px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
        }

        .alpp-close {
          position: absolute;
          top: -14px;
          right: -14px;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: none;
          background: #fff;
          color: #1a1a1a;
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
          transition: transform 0.2s, background 0.2s, color 0.2s;
        }
        .alpp-close:hover {
          transform: scale(1.08);
          background: var(--vibe-color-primary, #1a1a1a);
          color: var(--vibe-color-text-on-primary, #fff);
        }

        /* ── Mobile ── */
        @media (max-width: 640px) {
          .alpp-overlay { padding: 0.85rem; }
          .alpp-modal { max-width: 100%; max-height: 88vh; }
          .alpp-image { min-height: 200px; max-height: 80vh; }
          .alpp-close {
            top: 6px;
            right: 6px;
            width: 34px;
            height: 34px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .alpp-overlay { animation: none; background: rgba(0, 0, 0, 0.6); }
          .alpp-modal { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PromoPopup',
  displayName: 'Promo Popup',
  tokens: {
    'promopopup.image': {
      label: 'Popup Image',
      type: 'image',
      defaultValue: '',
    },
  },
  slotGroups: {
    'promopopup.link': linkDef,
  },
};

(PromoPopup as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PromoPopup;
